'use client';

/**
 * UnifiedSidebar Component
 *
 * Config-driven sidebar navigation that works across all portals.
 * Supports capability-based, role-based, and permission-based visibility filtering.
 * Supports lock states for tier/permission-gated features (Issue #383).
 *
 * @see Issue #330 - Unified Navigation System
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BarChartIcon from '@mui/icons-material/BarChart';
import BoltIcon from '@mui/icons-material/Bolt';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BusinessIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChatIcon from '@mui/icons-material/Chat';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContactsIcon from '@mui/icons-material/Contacts';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DescriptionIcon from '@mui/icons-material/Description';
import DevicesIcon from '@mui/icons-material/Devices';
import DomainIcon from '@mui/icons-material/Domain';
import EmailIcon from '@mui/icons-material/Email';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExtensionIcon from '@mui/icons-material/Extension';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HistoryIcon from '@mui/icons-material/History';
import HomeIcon from '@mui/icons-material/Home';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import HubIcon from '@mui/icons-material/Hub';
import InboxIcon from '@mui/icons-material/Inbox';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import KeyIcon from '@mui/icons-material/Key';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PageviewIcon from '@mui/icons-material/Pageview';
import PaidIcon from '@mui/icons-material/Paid';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PieChartIcon from '@mui/icons-material/PieChart';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import PsychologyIcon from '@mui/icons-material/Psychology';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RadarIcon from '@mui/icons-material/Radar';
import RuleIcon from '@mui/icons-material/Rule';
import SchemaIcon from '@mui/icons-material/Schema';
import SchoolIcon from '@mui/icons-material/School';
import ScienceIcon from '@mui/icons-material/Science';
import SecurityIcon from '@mui/icons-material/Security';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TableRowsIcon from '@mui/icons-material/TableRows';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WebhookIcon from '@mui/icons-material/Webhook';
import WidgetsIcon from '@mui/icons-material/Widgets';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { LockState, NavItem, NavSection, SidebarConfig, VisibilityContext } from '@ops-matrix/shared-types';
import { checkVisibility, filterNavSections } from '@ops-matrix/shared-types';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getTierLabel, usePermissionOptional } from '../../providers/PermissionProvider';
import { iconSizes } from '../../theme/typography';
import { HEADER_HEIGHT } from '../AppNavBar/AppNavBar';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Icon Mapping
// ============================================================================

const ICON_MAP: Record<string, React.ReactElement> = {
  // Core navigation
  Home: <HomeIcon />,
  Dashboard: <DashboardIcon />,
  SpaceDashboard: <SpaceDashboardIcon />,
  Widgets: <WidgetsIcon />,
  Settings: <SettingsIcon />,
  SettingsInputComponent: <SettingsInputComponentIcon />,
  Notifications: <NotificationsIcon />,
  Bolt: <BoltIcon />,
  Bookmark: <BookmarkIcon />,
  Pageview: <PageviewIcon />,
  Visibility: <VisibilityIcon />,
  Inbox: <InboxIcon />,

  // Organization & hierarchy
  Domain: <DomainIcon />,
  Business: <BusinessIcon />,
  AccountTree: <AccountTreeIcon />,
  Schema: <SchemaIcon />,

  // People & teams
  People: <PeopleIcon />,
  Groups: <GroupsIcon />,
  Group: <GroupIcon />,
  Contacts: <ContactsIcon />,
  PersonSearch: <PersonSearchIcon />,
  HowToReg: <HowToRegIcon />,

  // Documents & files
  Description: <DescriptionIcon />,
  Folder: <FolderIcon />,
  FolderOpen: <FolderOpenIcon />,
  FolderShared: <FolderSharedIcon />,
  FileCopy: <FileCopyIcon />,
  ContentCopy: <ContentCopyIcon />,
  Assignment: <AssignmentIcon />,

  // AI & automation
  AutoAwesome: <AutoAwesomeIcon />,
  Psychology: <PsychologyIcon />,
  AutoFixHigh: <AutoFixHighIcon />,
  SmartToy: <SmartToyIcon />,
  Hub: <HubIcon />,

  // Communication
  Chat: <ChatIcon />,
  Email: <EmailIcon />,
  Send: <SendIcon />,
  Campaign: <CampaignIcon />,

  // Pipeline & workflow
  Timeline: <TimelineIcon />,
  Leaderboard: <LeaderboardIcon />,
  PlaylistAddCheck: <PlaylistAddCheckIcon />,
  PlaylistPlay: <PlaylistPlayIcon />,
  TableRows: <TableRowsIcon />,
  HourglassEmpty: <HourglassEmptyIcon />,
  History: <HistoryIcon />,
  Rule: <RuleIcon />,
  Checklist: <ChecklistIcon />,

  // Catalog & schema
  Inventory2: <Inventory2Icon />,
  DataObject: <DataObjectIcon />,

  // Finance & billing
  CreditCard: <CreditCardIcon />,
  Payment: <PaymentIcon />,
  AccountBalance: <AccountBalanceIcon />,
  AttachMoney: <AttachMoneyIcon />,
  Paid: <PaidIcon />,

  // Partnerships
  Handshake: <HandshakeIcon />,
  Gavel: <GavelIcon />,

  // Commerce
  Storefront: <StorefrontIcon />,

  // Security & compliance
  Security: <SecurityIcon />,
  VerifiedUser: <VerifiedUserIcon />,
  Lock: <LockIcon />,
  Key: <KeyIcon />,
  Fingerprint: <FingerprintIcon />,

  // Monitoring & analytics
  MonitorHeart: <MonitorHeartIcon />,
  BarChart: <BarChartIcon />,
  Assessment: <AssessmentIcon />,
  ShowChart: <ShowChartIcon />,
  PieChart: <PieChartIcon />,
  QueryStats: <QueryStatsIcon />,
  Equalizer: <EqualizerIcon />,
  TrendingUp: <TrendingUpIcon />,
  Radar: <RadarIcon />,
  TrackChanges: <TrackChangesIcon />,

  // Development & testing
  Science: <ScienceIcon />,
  SportsEsports: <SportsEsportsIcon />,
  PlayCircle: <PlayCircleIcon />,

  // Integrations
  Extension: <ExtensionIcon />,
  Link: <LinkIcon />,
  Webhook: <WebhookIcon />,
  Devices: <DevicesIcon />,

  // Education & help
  School: <SchoolIcon />,

  // Actions
  Add: <AddIcon />,
};

function getIcon(iconName: string): React.ReactElement {
  return ICON_MAP[iconName] || <DashboardIcon />;
}

// ============================================================================
// Types
// ============================================================================

export interface UnifiedSidebarProps {
  /** Sidebar configuration */
  config: SidebarConfig;

  /** Current pathname for active state detection */
  pathname: string;

  /** Whether sidebar is open (for mobile drawer) */
  open: boolean;

  /** Toggle callback for mobile drawer */
  onToggle: () => void;

  /** Navigation callback (framework-agnostic) */
  onNavigate: (path: string) => void;

  /** Visibility context for filtering items */
  visibilityContext?: VisibilityContext;

  /** Custom header content (logo, business name) */
  header?: React.ReactNode;

  /** Custom footer content (user menu) */
  footer?: React.ReactNode;

  /** Dynamic badge counts by item ID */
  badgeCounts?: Record<string, number>;

  /** Variant override for testing */
  variant?: 'temporary' | 'permanent';

  /** Handler for logo/header click (typically navigate to dashboard) */
}

// ============================================================================
// Component
// ============================================================================

export const UnifiedSidebar: React.FC<UnifiedSidebarProps> = ({
  config,
  pathname,
  open,
  onToggle,
  onNavigate,
  visibilityContext = {},
  header,
  footer,
  badgeCounts = {},
  variant: variantOverride,
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const variant = variantOverride ?? (isMobile ? 'temporary' : 'permanent');

  const width = config.width ?? 240;
  const storageKey = config.storageKey ?? 'sidebar-expanded-sections';

  // Expanded sections state (for collapsible sections)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return new Set(JSON.parse(saved));
      // Default: expand collapsible sections that aren't defaultCollapsed
      const defaultExpanded = config.sections.filter((s) => s.collapsible && !s.defaultCollapsed).map((s) => s.id);
      return new Set(defaultExpanded);
    } catch {
      return new Set();
    }
  });

  // Persist expanded sections
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...expandedSections]));
    } catch {
      // Ignore storage errors
    }
  }, [expandedSections, storageKey]);

  // Filter sections based on visibility context
  const filteredSections = useMemo(() => {
    const allSections = [
      ...(config.standaloneItems ? [{ id: '__standalone__', items: config.standaloneItems }] : []),
      ...config.sections,
    ] as NavSection[];
    return filterNavSections(allSections, visibilityContext);
  }, [config.sections, config.standaloneItems, visibilityContext]);

  // Check if a path is active (exact match for sidebar items)
  const isPathActive = useCallback(
    (path: string): boolean => {
      // Exact match for sidebar navigation items
      return pathname === path;
    },
    [pathname],
  );

  // Check if an item is active (considering its tabs)
  const isItemActive = useCallback(
    (item: NavItem): boolean => {
      if (isPathActive(item.path)) return true;
      if (item.tabs) {
        return item.tabs.some((tab) => isPathActive(tab.path));
      }
      return false;
    },
    [isPathActive],
  );

  // Toggle section expansion
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Handle item click
  const handleItemClick = useCallback(
    (item: NavItem) => {
      // If item has tabs, navigate to the first visible tab
      if (item.tabs && item.tabs.length > 0) {
        const firstVisibleTab = item.tabs.find(
          (tab) => !tab.visibility || checkVisibility(tab.visibility, visibilityContext),
        );
        if (firstVisibleTab) {
          onNavigate(firstVisibleTab.path);
        } else {
          onNavigate(item.path);
        }
      } else {
        onNavigate(item.path);
      }

      // Close mobile drawer after navigation
      if (isMobile) {
        onToggle();
      }
    },
    [onNavigate, visibilityContext, isMobile, onToggle],
  );

  // Get permission context for lock state checking
  const permissionContext = usePermissionOptional();

  // Compute lock state for an item
  const getItemLockState = useCallback(
    (item: NavItem): LockState | null => {
      if (!item.lock || !permissionContext) return null;
      return permissionContext.getLockState(item.lock);
    },
    [permissionContext],
  );

  // Handle click on locked item
  const handleLockedItemClick = useCallback((lockState: LockState) => {
    if (lockState.onLockedClick) {
      lockState.onLockedClick();
    }
    // Default behavior is handled by PermissionProvider's onTierLockClick/onPermissionLockClick
  }, []);

  // Shared Liquid Glass active/hover state (used by nav items and settings button)
  const glassActiveStateSx = useMemo(
    () => ({
      '&.Mui-selected': {
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(99, 91, 255, 0.08)',
        color: 'primary.main',
        fontWeight: 600,
        borderRadius: '8px',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 0 8px rgba(99,91,255,0.10), inset 0 0.5px 0 rgba(255,255,255,0.08)'
            : '0 0 8px rgba(99,91,255,0.08), inset 0 0.5px 0 rgba(255,255,255,0.3)',
        '& .MuiListItemIcon-root': {
          color: 'primary.main',
        },
        '&:hover': {
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(99, 91, 255, 0.12)',
        },
      },
      '&:hover': {
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      },
    }),
    [theme.palette.mode],
  );

  // Render a navigation item
  const renderNavItem = (item: NavItem, dense = false, hideIcon = false) => {
    const isActive = isItemActive(item);
    const label = t(item.label) || item.label;
    const badgeCount = badgeCounts[item.id];

    // Check if item is locked (Issue #383)
    const lockState = getItemLockState(item);
    const isLocked = !!lockState;

    // Handle click - either navigate or trigger lock handler
    const handleClick = () => {
      if (isLocked) {
        handleLockedItemClick(lockState!);
        return;
      }
      if (item.comingSoon) return;
      handleItemClick(item);
    };

    const content = (
      <ListItem key={item.id} disablePadding>
        <ListItemButton
          selected={isActive && !isLocked}
          onClick={handleClick}
          disabled={item.disabled}
          sx={{
            py: dense ? 0.75 : 1,
            px: 2,
            borderRadius: '8px',
            mx: 1,
            pl: hideIcon ? 3 : undefined,
            position: 'relative',
            opacity: isLocked ? 0.7 : 1,
            cursor: isLocked ? 'pointer' : undefined,
            transition: 'all 0.15s ease',
            // Sub-items: line segment
            ...(hideIcon && {
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 12,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: isActive && !isLocked ? 'primary.main' : 'transparent',
                borderRadius: 1,
                zIndex: 1,
                transition: 'background-color 0.2s ease',
              },
            }),
            ...glassActiveStateSx,
          }}
        >
          {!hideIcon && (
            <ListItemIcon sx={{ minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.125rem' } }}>
              {getIcon(item.icon)}
            </ListItemIcon>
          )}
          <ListItemText
            primary={label}
            slotProps={{
              primary: {
                color: isLocked ? 'text.disabled' : undefined,
                sx: {
                  fontSize: dense ? '0.875rem' : '0.9375rem', // 15pt Apple body
                  fontWeight: isActive && !isLocked ? 600 : 400,
                },
              },
            }}
          />
          {/* Lock indicator (Issue #383) */}
          {isLocked && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <LockIcon
                sx={{
                  fontSize: iconSizes.sm,
                  color:
                    lockState.type === 'tier'
                      ? 'warning.main'
                      : lockState.type === 'license'
                        ? 'info.main'
                        : 'action.disabled',
                }}
              />
              {lockState.type === 'tier' && lockState.requiredTier && (
                <GlassChip
                  label={getTierLabel(lockState.requiredTier)}
                  size="small"
                  color="warning"
                  sx={{
                    height: 18,
                    fontSize: '0.75rem',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              )}
            </Box>
          )}
          {!isLocked && !item.comingSoon && item.tabs && item.tabs.length > 0 && (
            <ChevronRightIcon sx={{ fontSize: '1rem', opacity: 0.5 }} />
          )}
          {!isLocked && item.comingSoon && (
            <GlassChip
              label={t('common.soon')}
              size="small"
              variant="outlined"
              sx={{
                ml: 'auto',
                height: 16,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                flexShrink: 0,
                bgcolor: 'transparent',
                borderColor: 'transparent',
                color: 'info.main',
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
          {!isLocked && !item.comingSoon && badgeCount !== undefined && badgeCount > 0 && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                ml: 1,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'error.main',
                color: 'error.contrastText',
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1,
                minWidth: 20,
              }}
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </Box>
          )}
        </ListItemButton>
      </ListItem>
    );

    // Wrap with tooltip if locked
    if (isLocked) {
      return (
        <GlassTooltip key={item.id} title={lockState.reason} arrow placement="right">
          <span>{content}</span>
        </GlassTooltip>
      );
    }

    return content;
  };

  // Render a collapsible section
  const renderCollapsibleSection = (section: NavSection) => {
    const isExpanded = expandedSections.has(section.id);
    const hasActiveItem = section.items.some(isItemActive);
    const sectionLabel = section.label ? t(section.label) || section.label : '';

    return (
      <Box key={section.id}>
        {section.label && (
          <ListItemButton
            onClick={() => toggleSection(section.id)}
            sx={{
              py: 1,
              px: 2,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {section.icon && <ListItemIcon sx={{ minWidth: 36 }}>{getIcon(section.icon)}</ListItemIcon>}
            <ListItemText
              primary={sectionLabel}
              slotProps={{
                primary: {
                  color: hasActiveItem ? 'primary.main' : 'text.secondary',
                  sx: {
                    fontSize: '0.8125rem', // 13pt Apple caption
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  },
                },
              }}
            />
            {isExpanded ? (
              <ExpandLessIcon sx={{ fontSize: '1.25rem' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: '1.25rem' }} />
            )}
          </ListItemButton>
        )}
        <Collapse in={isExpanded || !section.label} timeout="auto" unmountOnExit={false}>
          <Box
            sx={{
              position: 'relative',
              '&::before':
                isExpanded && section.label
                  ? {
                      content: '""',
                      position: 'absolute',
                      left: 20,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      bgcolor: 'divider',
                      borderRadius: 1,
                    }
                  : undefined,
            }}
          >
            <List dense disablePadding>
              {section.items.map((item) => renderNavItem(item, true, !!section.label))}
            </List>
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Render a flat section (no collapsing)
  const renderFlatSection = (section: NavSection, index: number) => {
    const sectionLabel = section.label ? t(section.label) || section.label : '';

    return (
      <Box key={section.id}>
        {sectionLabel && (
          <Typography
            variant="overline"
            sx={{
              px: 2,
              pt: index === 0 ? 1 : 2,
              pb: 0.5,
              display: 'block',
              color: 'text.secondary',
              fontSize: '0.8125rem', // 13pt
              fontWeight: 600,
              letterSpacing: '0.05em',
            }}
          >
            {sectionLabel}
          </Typography>
        )}
        <List dense={!!sectionLabel} disablePadding>
          {section.items.map((item) => renderNavItem(item, !!sectionLabel))}
        </List>
      </Box>
    );
  };

  // When the drawer is closed (`open=false`), the permanent variant slides the
  // paper off-screen via translateX but leaves the entire nav subtree in the
  // DOM. Without `inert` + `aria-hidden` + `visibility: hidden`, screen
  // readers still announce all the off-screen nav links and Playwright /
  // `document.elementFromPoint` can still resolve `getByText(...)` inside the
  // collapsed paper — the click coordinates then land on whatever element
  // happens to occupy that screen position (the SidebarRail, the page chrome,
  // a button on the underlying page), surfacing as a "click intercepted by
  // MuiDrawer pointer events" failure or, worse, a misdirected click on the
  // underlying page. Mirrors the SidebarPanel fix (commit 841658e9c).
  const isInert = !open;

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onToggle}
      slotProps={{
        paper: {
          // `inert` is a React 19 native attribute; it removes the subtree
          // from focus, hit-test, and the a11y tree in one shot.
          inert: isInert || undefined,
          'aria-hidden': isInert || undefined,
        },
      }}
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
          // Liquid Glass sidebar: hairline border, glass background
          borderRight: `0.5px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          // Unified glass background (matches AppNavBar, SidebarRail, SidebarPanel)
          background: theme.palette.mode === 'dark' ? 'rgba(18, 18, 20, 0.92)' : 'rgba(245, 245, 248, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          overflowX: 'hidden',
          // Use transform to slide drawer in/out
          transform: open ? 'translateX(0)' : `translateX(-${width}px)`,
          // `visibility: hidden` strips the subtree from the hit-test tree
          // and a11y tree (in addition to `inert`). Delay the flip on close
          // (0.25s) so the slide-out animation can play; on open it's
          // instant so the panel becomes interactive as it slides in.
          visibility: isInert ? 'hidden' : 'visible',
          pointerEvents: isInert ? 'none' : 'auto',
          transition: `${theme.transitions.create('transform', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          })}, visibility 0s linear ${isInert ? '0.25s' : '0s'}`,
        },
      }}
    >
      {/* Spacer so navigation starts below the fixed AppNavBar */}
      {header ?? <Box sx={{ minHeight: HEADER_HEIGHT + 8, flexShrink: 0 }} />}
      {/* Navigation */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {filteredSections.map((section, index) =>
          section.collapsible ? renderCollapsibleSection(section) : renderFlatSection(section, index),
        )}
      </Box>
      {/* Settings shortcut */}
      {config.settingsNav && (
        <>
          <Divider />
          <List>
            <ListItem disablePadding>
              <GlassTooltip
                title={config.settingsNav.tooltip ? t(config.settingsNav.tooltip) : t('navigation.settings')}
              >
                <span>
                  <ListItemButton
                    onClick={() => {
                      onNavigate(config.settingsNav!.path);
                      if (isMobile) onToggle();
                    }}
                    selected={isPathActive(config.settingsNav.path)}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      px: 2,
                      position: 'relative',
                      transition: 'all 0.15s ease',
                      ...glassActiveStateSx,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.125rem' } }}>
                      <TuneIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={config.settingsNav.tooltip ? t(config.settingsNav.tooltip) : t('navigation.settings')}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: '0.9375rem',
                            fontWeight: isPathActive(config.settingsNav.path) ? 600 : 400,
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                </span>
              </GlassTooltip>
            </ListItem>
          </List>
        </>
      )}
      {/* Footer */}
      {footer}
    </Drawer>
  );
};

export default UnifiedSidebar;
